import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { type TraversedDocument, getTraverseCustomerDocuments } from '@/service/PrismicService';

import { hasCategoryViewFlags } from '@/utils/customer-view-utils.ts';
import { isProcessedPrismicValues } from '@/utils/helpers';

import { usePersistedPrismicStore, usePrismicStore, useSearchStore } from '@/store';

const loadedLangs: Record<string, boolean> = {};

interface DocumentIndex {
    documentsById: Record<string, Record<string, unknown>>;
    hubsByKey: Record<string, Record<string, unknown>>;
    professionalsByKey: Record<string, Record<string, unknown>>;
    specialtiesByKey: Record<string, Record<string, unknown>>;
    treatmentCategoriesByKey: Record<string, Record<string, unknown>>;
}

function indexDocuments(documents: TraversedDocument[]): DocumentIndex {
    const index: DocumentIndex = {
        documentsById: {},
        hubsByKey: {},
        professionalsByKey: {},
        specialtiesByKey: {},
        treatmentCategoriesByKey: {},
    };

    const environment = import.meta.env.VITE_ENVIRONMENT;

    for (const doc of documents) {
        const data = doc.data as Record<string, unknown>;
        const key = (data.key as Array<{ text: string }>)?.[0]?.text;

        index.documentsById[doc.id] = data;

        switch (doc.type) {
            case 'hub':
                if (key) index.hubsByKey[key] = { ...data, prismicId: doc.id };
                break;

            case 'professional_profile':
                if (key && doc.tags.includes(environment) && data.is_bookable) {
                    index.professionalsByKey[key] = data;
                }
                break;

            case 'professional_type':
                if (key) index.specialtiesByKey[key] = data;
                break;

            case 'treatment_type_category':
                if (key) index.treatmentCategoriesByKey[key] = data;
                break;
        }
    }

    return index;
}

function extractCustomerConfig(documents: TraversedDocument[]): Record<string, unknown> {
    const doc = documents.find((d) => d.type === 'customer_config');
    return doc!.data as Record<string, unknown>;
}

function extractProfessionalProfiles(documents: TraversedDocument[]): Record<string, unknown>[] {
    return documents
        .filter((doc) => doc.type === 'professional_profile')
        .map((doc) => doc.data as Record<string, unknown>);
}

function extractPageProfessionals(documents: TraversedDocument[]): Record<string, unknown> | undefined {
    const doc = documents.find((d) => d.type === 'page_professionals');
    return doc?.data as Record<string, unknown> | undefined;
}

function buildHubsAndTreatments(index: DocumentIndex): {
    hubs: Record<string, Record<string, unknown>>;
    treatments: Record<string, Record<string, unknown>>;
} {
    const treatments: Record<string, Record<string, unknown>> = {};

    for (const [hubKey, hub] of Object.entries(index.hubsByKey)) {
        const professionalTypes = hub.professional_types as Array<{ professional_type: { id: string } }>;
        if (!Array.isArray(professionalTypes)) continue;

        const enrichedTypes: Record<string, Record<string, unknown>> = {};

        for (const pt of professionalTypes) {
            const ptData = index.documentsById[pt.professional_type?.id] as Record<string, unknown>;
            if (!ptData) continue;

            const ptKey = (ptData.key as Array<{ text: string }>)?.[0]?.text;
            if (!ptKey) continue;

            const servicesOrTreatments = (ptData.services_or_treatments || []) as Array<Record<string, unknown>>;

            const enrichedServices = servicesOrTreatments
                .filter((service) => Boolean(service) && typeof service === 'object')
                .map((service) => {
                const serviceRef = service.service_or_treatment as { id: string };
                const categoryRef = service.category as { id: string } | undefined;

                const serviceData = index.documentsById[serviceRef?.id] || serviceRef;
                const categoryData = categoryRef?.id ? index.documentsById[categoryRef.id] : categoryRef;

                const enrichedService = {
                    ...service,
                    service_or_treatment: serviceData,
                    category: categoryData,
                    specialty: ptKey,
                };

                // Build treatments index
                const treatmentKey = (serviceData?.key as Array<{ text: string }>)?.[0]?.text;
                if (treatmentKey) {
                    treatments[hubKey] = treatments[hubKey] || {};
                    treatments[hubKey][treatmentKey] = { ...enrichedService, ...serviceData };
                }

                return enrichedService;
            });

            enrichedTypes[ptKey] = { ...ptData, services_or_treatments: enrichedServices };
        }

        hub.professional_types = enrichedTypes;
    }

    return { hubs: index.hubsByKey, treatments };
}

function buildProfessionalProfiles(
    profiles: Record<string, unknown>[],
    documentsById: Record<string, Record<string, unknown>>
): Record<string, unknown>[] {
    return profiles.map((profileData) => {
        const data = { ...profileData };

        // add location data
        const locationHub = data.location_hub as { id: string } | undefined;
        if (locationHub?.id) {
            data.locationData = documentsById[locationHub.id];
        }

        // add specialty type data
        const typeRef = data.type as { id: string } | undefined;
        if (typeRef?.id) {
            data.specialityType = documentsById[typeRef.id];
        }

        // add services list
        const services = data.services as Array<{ service: { id: string } }> | undefined;
        if (services) {
            data.servicesList = services
                .map((svc) => (svc.service?.id ? documentsById[svc.service.id] : null))
                .filter(Boolean);
        }

        return data;
    });
}

function buildCustomer(
    customerData: Record<string, unknown>,
    hubs: Record<string, Record<string, unknown>>,
    treatments: Record<string, Record<string, unknown>>,
    documentsById: Record<string, Record<string, unknown>>
): { key: string; customerId: string | undefined; customer: Record<string, unknown> } | null {
    const key = (customerData.key as Array<{ text: string }>)?.[0]?.text;
    if (!key) return null;

    // resolve locations
    const locationRefs = (customerData.locations || []) as Array<{ location: { id: string } }>;
    const locations = locationRefs
        .map((loc) => {
            const locationData = documentsById[loc.location?.id];
            const locationKey = (locationData?.key as Array<{ text: string }>)?.[0]?.text;
            return locationKey ? hubs[locationKey] : undefined;
        })
        .filter(Boolean);

    const customer: Record<string, unknown> = { ...customerData, locations };

    // build categories for the category browser and accordion views
    if (hasCategoryViewFlags(customerData)) {
        const categories: Record<string, Record<string, unknown>> = {};

        for (const location of locations) {
            const loc = location as Record<string, unknown>;
            const professionalTypes = loc.professional_types as Record<string, Record<string, unknown>> | null;
            if (!professionalTypes || typeof professionalTypes !== 'object') continue;

            for (const specialty of Object.values(professionalTypes)) {
                if (!specialty || typeof specialty !== 'object') continue;
                const services = specialty.services_or_treatments as Array<Record<string, unknown>>;
                if (!Array.isArray(services)) continue;

                for (const treatment of services) {
                    if (!treatment || typeof treatment !== 'object') continue;
                    const categoryKey = (
                        (treatment.category as Record<string, unknown>)?.key as Array<{ text: string }>
                    )?.[0]?.text;
                    const treatmentKey = (
                        (treatment.service_or_treatment as Record<string, unknown>)?.key as Array<{ text: string }>
                    )?.[0]?.text;
                    const locationKey = (loc.key as Array<{ text: string }>)?.[0]?.text;

                    if (categoryKey && treatmentKey && locationKey) {
                        categories[categoryKey] = categories[categoryKey] || {};
                        categories[categoryKey][treatmentKey] = treatments[locationKey]?.[treatmentKey];
                    }
                }
            }
        }

        customer.categories = categories;
    }

    const customerId = (customerData.customer_id as Array<{ text: string }>)?.[0]?.text;

    return { key, customerId, customer };
}

function buildHubsContent(hubs: Record<string, Record<string, unknown>>): Record<string, unknown> {
    const content: Record<string, unknown> = {};

    for (const [key, hub] of Object.entries(hubs)) {
        if (hub.is_activated) {
            content[key] = {
                title: (hub.title as Array<{ text: string }>)?.[0]?.text,
                eterno_map_title: (hub.eterno_map_title as Array<{ text: string }>)?.[0]?.text,
                image_url: (hub.hub_image as { url: string })?.url,
                mobile_image_url: (hub.hub_mobile_image as { url: string })?.url,
            };
        }
    }

    return content;
}

export default function usePrismicCustomerData(): void {
    const { i18n } = useTranslation();
    const hasHydrated = usePersistedPrismicStore((state) => state._hasHydrated);
    const loadingRef = useRef<Record<string, boolean>>({});

    useEffect(() => {
        if (!hasHydrated || loadedLangs[i18n.language] || loadingRef.current[i18n.language]) return;
        loadingRef.current[i18n.language] = true;

        function buildContentHeading(documents: TraversedDocument[]) {
            const findAnAppointment = documents.find((document) => document.type === 'find_an_appointment');

            return findAnAppointment ? [isProcessedPrismicValues(findAnAppointment.data)] : [];
        }

        const loadCustomerData = async () => {
            const tag = import.meta.env.VITE_ENVIRONMENT;
            const lang = i18n.language === 'en' ? 'en-us' : 'de-de';
            const defaultCustomerKey = import.meta.env.VITE_ETERNO_CUSTOMER_KEY;

            // extract customer key from URL
            const match = window.location.pathname.match(/\/customer\/([^/]+)/);
            const customerKey = match ? match[1] : defaultCustomerKey;

            let result = await getTraverseCustomerDocuments(customerKey, tag, lang);

            // fallback to default customer if not found and let the router redirect to eterno cloud due to missing config
            // without any data fetched the spinner will show forever so this is least invasive fallback without modyfying the router and the state
            if (!result.success && customerKey !== defaultCustomerKey) {
                result = await getTraverseCustomerDocuments(defaultCustomerKey, tag, lang);
            }

            // index documents for lookups
            const index = indexDocuments(result.documents);

            // extract non-indexed documents
            const customerConfig = extractCustomerConfig(result.documents);
            const rawProfiles = extractProfessionalProfiles(result.documents);
            const pageProfessionalsData = extractPageProfessionals(result.documents);

            // build store data
            const { hubs, treatments } = buildHubsAndTreatments(index);
            const professionalProfiles = buildProfessionalProfiles(rawProfiles, index.documentsById);
            const hubsContent = buildHubsContent(hubs);
            const customerResult = buildCustomer(customerConfig, hubs, treatments, index.documentsById);
            if (!customerResult) {
                console.error('[usePrismicCustomerData] Failed to build customer');
                return;
            }
            const contentHeading = buildContentHeading(result.documents);

            // update prismic store
            usePrismicStore.setState({
                customers: { [customerResult.key]: customerResult.customer },
                customersById: customerResult.customerId
                    ? { [customerResult.customerId]: customerResult.customer }
                    : {},
            } as any);

            useSearchStore.setState({
                locations: hubs,
                professionals: index.professionalsByKey,
                specialties: index.specialtiesByKey,
                treatments: treatments,
                treatmentTypeCategories: index.treatmentCategoriesByKey,
                allDocuments: index.documentsById,
            } as any);

            const {
                findAppointmentData: currentFindAppointmentData,
                bookAppointmentData,
                professionalProfileData: currentProfessionalProfileData,
            } = usePersistedPrismicStore.getState();
            usePersistedPrismicStore.setState({
                findAppointmentData: {
                    ...currentFindAppointmentData,
                    [i18n.language]: {
                        content: hubsContent,
                        contentHeading: contentHeading,
                        bookingHeading: bookAppointmentData[i18n.language].mainData,
                        professionalProfiles: professionalProfiles,
                        dataSet: true,
                        unprocessedData: null,
                    },
                },
                professionalProfileData: {
                    ...currentProfessionalProfileData,
                    [i18n.language]: {
                        mainData: pageProfessionalsData,
                        content: professionalProfiles,
                        dataSet: true,
                    },
                },
                // Set doctorData.dataSet to prevent usePrismicContent from fetching duplicate data
                doctorData: {
                    ...professionalProfiles,
                    [i18n.language]: {
                        ...professionalProfiles,
                        dataSet: true,
                    },
                },
            });

            loadedLangs[i18n.language] = true;
        };

        loadCustomerData();
    }, [hasHydrated, i18n.language]);
}
